import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Flex,
  Input,
  Row,
  Select,
  Space,
  Modal,
  Checkbox,
  InputNumber,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/auth';
import * as S from './styles';
import { getTenants, createTenant, ITenant } from '../../http/tenants';
import {
  getConfiguration,
  getConfigurationPlanSuggestions,
  updateConfiguration,
  IConfiguration,
  IPlan,
} from '../../http/configurations';
import { createPlan, getPlans, updatePlan } from '../../http/plans';
import { useToast } from '../../hooks/toast';
import { formatDate } from '../../shared/utils/format-date';
import TenantCreateEdit from './CreateEdit';
import { useI18n } from '../../shared/i18n';

const { Text, Title } = Typography;

export const PLAN_MODULE_KEYS = [
  'agenda',
  'clientes',
  'financeiro',
  'documentos',
  'assinatura_digital',
  'tratamentos',
  'odontologico',
  'atestados_medicos',
  'formularios',
  'guias',
  'convenios',
  'estetica',
  'estoque',
  'produtos',
  'usuarios',
  'permissoes',
  'profissionais',
  'especialidades',
  'procedimentos',
  'whatsapp',
  'email',
  'empresa',
] as const;

const PLAN_MODULE_ALIASES: Record<string, string[]> = {
  medico: ['atestados_medicos', 'formularios', 'guias', 'convenios'],
  documentos: ['documentos', 'assinatura_digital'],
  estoque: ['estoque', 'produtos'],
  usuarios: ['usuarios', 'permissoes'],
  profissionais: ['profissionais', 'especialidades'],
  comunicacao: ['whatsapp', 'email'],
};

const AUTO_CONFIRMATION_CHANNEL_VALUES = [
  'both',
  'whatsapp',
  'email',
  'disabled',
] as const;

const getModuleLabel = (
  key: string,
  t: (key: string, values?: Record<string, string | number>) => string,
) => t(`companiesPage.modules.${key}`);

const getAutoConfirmationChannelLabel = (
  value: string,
  t: (key: string, values?: Record<string, string | number>) => string,
) => t(`companiesPage.autoConfirmationChannels.${value}`);

const getTenantStatusLabel = (
  blocked: boolean,
  t: (key: string, values?: Record<string, string | number>) => string,
) =>
  blocked
    ? t('companiesPage.status.tenant.blocked')
    : t('companiesPage.status.tenant.active');

const getPlanStatusLabel = (
  value: string,
  t: (key: string, values?: Record<string, string | number>) => string,
) => t(`companiesPage.status.plan.${value}`);

const getPlanEnabledStatusLabel = (
  value: boolean,
  t: (key: string, values?: Record<string, string | number>) => string,
) =>
  value
    ? t('companiesPage.status.enabled.active')
    : t('companiesPage.status.enabled.inactive');

const normalizeModules = (modules?: string[] | null): string[] => {
  if (!Array.isArray(modules)) {
    return [];
  }

  return Array.from(
    new Set(
      modules
        .flatMap((item) => {
          const normalizedItem = item.trim();
          return PLAN_MODULE_ALIASES[normalizedItem] ?? [normalizedItem];
        })
        .filter(Boolean),
    ),
  );
};

const normalizePlanModules = (plan: IPlan): IPlan => ({
  ...plan,
  allowed_modules: normalizeModules(plan.allowed_modules),
});

const normalizeConfigurationModules = (
  configuration: IConfiguration,
): IConfiguration => ({
  ...configuration,
  auto_schedule_confirmation_channel:
    configuration.auto_schedule_confirmation_channel || 'both',
  allowed_modules: normalizeModules(configuration.allowed_modules),
  override_allowed_modules:
    configuration.override_allowed_modules === null ||
    configuration.override_allowed_modules === undefined
      ? configuration.override_allowed_modules
      : normalizeModules(configuration.override_allowed_modules),
  plan: configuration.plan ? normalizePlanModules(configuration.plan) : null,
});

const emptyPlanForm: Partial<IPlan> = {
  code: '',
  name: '',
  description: '',
  landing_subtitle: '',
  landing_price_label: '',
  landing_frequency_label: '/mês',
  landing_highlight: '',
  landing_features: [],
  landing_sort_order: 0,
  landing_visible: true,
  monthly_price_cents: 0,
  max_users: 1,
  max_professionals: 1,
  max_clients: 100,
  recommended_due_date: 5,
  recommended_show_client_code: false,
  recommended_dental: false,
  recommended_medical: false,
  recommended_aesthetics: false,
  allowed_modules: [],
  status: true,
};

const planFeaturesToText = (features?: string[] | null) =>
  Array.isArray(features) ? features.join('\n') : '';

const planFeaturesFromText = (value: string) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, getToken } = useAuth();
  const { addToast } = useToast();
  const { t } = useI18n();
  const [tenants, setTenants] = useState<ITenant[]>([]);
  const [configs, setConfigs] = useState<Record<string, IConfiguration>>({});
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [modalCreateVisible, setModalCreateVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<ITenant | null>(null);
  const [config, setConfig] = useState<IConfiguration | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [planSuggestions, setPlanSuggestions] = useState<IPlan[]>([]);
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState<IPlan | null>(null);
  const [planForm, setPlanForm] = useState<Partial<IPlan>>(emptyPlanForm);

  const fetchTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const data = await getTenants();
      setTenants(data);
      const token = getToken();
      const configPromises = data.map((tenant) =>
        getConfiguration(tenant.name, token).catch(() => null),
      );
      const configsArray = await Promise.all(configPromises);
      const configsObj: Record<string, IConfiguration> = {};
      configsArray.forEach((itemConfig, index) => {
        if (itemConfig) {
          configsObj[data[index].id] = itemConfig;
        }
      });
      setConfigs(configsObj);
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
      addToast({
        type: 'error',
        title: t('companiesPage.toast.errorTitle'),
        description: t('companiesPage.toast.fetchCompaniesError'),
      });
    } finally {
      setLoadingTenants(false);
    }
  }, [addToast, getToken, t]);

  const fetchPlansList = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const data = await getPlans();
      setPlans(data);
    } catch (err) {
      console.error('Erro ao buscar planos:', err);
      addToast({
        type: 'error',
        title: t('companiesPage.toast.errorTitle'),
        description: t('companiesPage.toast.fetchPlansError'),
      });
    } finally {
      setLoadingPlans(false);
    }
  }, [addToast, t]);

  const checkPermission = useCallback(() => {
    if (isSuperAdmin) {
      setIsAuthorized(true);
      fetchTenants();
      fetchPlansList();
    } else {
      setIsAuthorized(false);
    }
    setAuthChecking(false);
  }, [fetchTenants, fetchPlansList, isSuperAdmin]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const openConfigModal = async (tenant: ITenant) => {
    setSelectedTenant(tenant);
    setModalVisible(true);
    setLoadingConfig(true);
    try {
      const [conf, suggestions] = await Promise.all([
        getConfiguration(tenant.name, getToken()),
        getConfigurationPlanSuggestions(tenant.name, getToken()),
      ]);
      setConfig(conf ? normalizeConfigurationModules(conf) : null);
      setPlanSuggestions(suggestions.map(normalizePlanModules));
    } catch (err) {
      console.error('Erro ao buscar configurações:', err);
      setConfig(null);
      setPlanSuggestions([]);
    } finally {
      setLoadingConfig(false);
    }
  };

  const applyPlanSuggestion = (planId?: string) => {
    if (!config) return;

    if (!planId) {
      setConfig({
        ...config,
        plan_id: undefined,
        plan: null,
      });
      return;
    }

    const selectedPlan = planSuggestions.find((item) => item.id === planId);
    if (!selectedPlan) return;

    setConfig({
      ...config,
      plan_id: selectedPlan.id,
      plan: selectedPlan,
      due_date: selectedPlan.recommended_due_date,
      dental: selectedPlan.recommended_dental,
      medical: selectedPlan.recommended_medical,
      aesthetics: selectedPlan.recommended_aesthetics,
      show_client_code: selectedPlan.recommended_show_client_code,
      max_users: selectedPlan.max_users,
      max_professionals: selectedPlan.max_professionals,
      max_clients: selectedPlan.max_clients,
      allowed_modules: normalizeModules(selectedPlan.allowed_modules),
      override_allowed_modules: null,
    });
  };

  const handleSaveConfig = async () => {
    if (!selectedTenant || !config) return;
    setSavingConfig(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { plan: _plan, ...payload } = config;
      await updateConfiguration(
        selectedTenant.name,
        config.id,
        {
          ...payload,
          plan_started_at: payload.plan_started_at || undefined,
          plan_ends_at: payload.plan_ends_at || undefined,
        },
        getToken(),
      );
      setConfigs((prev) => ({ ...prev, [selectedTenant.id]: config }));
      setModalVisible(false);
      setConfig(null);
      setSelectedTenant(null);
      addToast({
        type: 'success',
        title: t('companiesPage.toast.successTitle'),
        description: t('companiesPage.toast.saveConfigSuccess'),
      });
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCreateTenant = async (name: string) => {
    setCreatingTenant(true);
    try {
      await createTenant(name);
      addToast({
        type: 'success',
        title: t('companiesPage.toast.successTitle'),
        description: t('companiesPage.toast.createTenantSuccess'),
      });
      await fetchTenants();
    } catch (err: any) {
      throw new Error(
        err?.response?.data?.message ||
          err?.message ||
          t('companiesPage.toast.serverConnectionError'),
      );
    } finally {
      setCreatingTenant(false);
    }
  };

  const openCreatePlanModal = () => {
    setEditingPlan(null);
    setPlanForm(emptyPlanForm);
    setPlanModalVisible(true);
  };

  const openEditPlanModal = (plan: IPlan) => {
    setEditingPlan(plan);
    setPlanForm(normalizePlanModules(plan));
    setPlanModalVisible(true);
  };

  const handleSavePlan = async () => {
    setSavingPlan(true);
    try {
      if (editingPlan?.id) {
        await updatePlan(editingPlan.id, planForm);
      } else {
        await createPlan(planForm);
      }

      addToast({
        type: 'success',
        title: t('companiesPage.toast.successTitle'),
        description: t('companiesPage.toast.savePlanSuccess'),
      });
      setPlanModalVisible(false);
      setEditingPlan(null);
      setPlanForm(emptyPlanForm);
      await fetchPlansList();
    } catch (err) {
      console.error('Erro ao salvar plano:', err);
      addToast({
        type: 'error',
        title: t('companiesPage.toast.errorTitle'),
        description: t('companiesPage.toast.savePlanError'),
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const autoConfirmationChannelOptions = useMemo(
    () =>
      AUTO_CONFIRMATION_CHANNEL_VALUES.map((value) => ({
        value,
        label: getAutoConfirmationChannelLabel(value, t),
      })),
    [t],
  );

  const tenantsSummary = useMemo(() => {
    const total = tenants.length;
    const blocked = tenants.filter((item) => configs[item.id]?.blocked).length;
    const active = total - blocked;

    return {
      total,
      blocked,
      active,
    };
  }, [tenants, configs]);

  const columns = [
    {
      title: t('companiesPage.table.tenants.columns.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('companiesPage.table.tenants.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag
          color={
            status === t('companiesPage.status.tenant.blocked')
              ? 'error'
              : 'success'
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: t('companiesPage.table.tenants.columns.activeModules'),
      dataIndex: 'modulesText',
      key: 'modulesText',
      render: (value: string) => {
        if (value === t('companiesPage.shared.none')) {
          return <Text type="secondary">{t('companiesPage.shared.none')}</Text>;
        }

        return (
          <Flex gap={4} wrap>
            {value.split(', ').map((item: string) => (
              <Tag key={item} color="blue" style={{ marginRight: 0 }}>
                {item}
              </Tag>
            ))}
          </Flex>
        );
      },
    },
    {
      title: t('companiesPage.table.tenants.columns.plan'),
      dataIndex: 'planName',
      key: 'planName',
      width: 180,
      render: (value: string) =>
        value || t('companiesPage.shared.notAvailable'),
    },
    {
      title: t('companiesPage.table.tenants.columns.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 150,
    },
    {
      title: t('companiesPage.table.tenants.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) =>
        date ? formatDate(date) : t('companiesPage.shared.notAvailable'),
    },
    {
      title: t('companiesPage.table.tenants.columns.actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => openConfigModal(record.tenant)}
          size="small"
        >
          {t('companiesPage.actions.configure')}
        </Button>
      ),
    },
  ];

  const dataSource = tenants.map((tenant) => {
    const config = configs[tenant.id];
    const status = getTenantStatusLabel(!!config?.blocked, t);

    let activeModules: string[] = [];
    const effectiveModules =
      config?.override_allowed_modules ?? config?.allowed_modules;
    if (Array.isArray(effectiveModules) && effectiveModules.length > 0) {
      activeModules = normalizeModules(effectiveModules).map((key) =>
        getModuleLabel(key, t),
      );
    } else {
      if (config?.dental)
        activeModules.push(t('companiesPage.legacyModules.dental'));
      if (config?.medical)
        activeModules.push(t('companiesPage.legacyModules.medical'));
      if (config?.aesthetics) {
        activeModules.push(t('companiesPage.legacyModules.aesthetics'));
      }
    }

    const modulesText =
      activeModules.length > 0
        ? activeModules.join(', ')
        : t('companiesPage.shared.none');
    const dueDate = config?.due_date || t('companiesPage.shared.notAvailable');
    const planName =
      config?.plan?.name || t('companiesPage.shared.notAvailable');

    return {
      key: tenant.id,
      id: tenant.id,
      name: tenant.name,
      status,
      modulesText,
      planName,
      dueDate,
      created_at: tenant.created_at,
      tenant, // para acessar no render
    };
  });

  if (authChecking) {
    return <S.Container>{t('companiesPage.permission.checking')}</S.Container>;
  }

  if (!isAuthorized) {
    return (
      <S.Container>
        <S.AccessDeniedCard>
          <h2>{t('companiesPage.permission.deniedTitle')}</h2>
          <p>{t('companiesPage.permission.deniedDescription')}</p>
          <p>{t('companiesPage.permission.deniedHelper')}</p>
        </S.AccessDeniedCard>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <Breadcrumb style={{ marginBottom: '20px' }}>
        <Breadcrumb.Item>
          <Link to="/dashboard">{t('companiesPage.breadcrumb.home')}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {t('companiesPage.breadcrumb.current')}
        </Breadcrumb.Item>
      </Breadcrumb>
      <S.HeaderContainer>
        <S.Title>{t('companiesPage.header.title')}</S.Title>
      </S.HeaderContainer>

      <S.Card>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Text type="secondary">{t('companiesPage.summary.total')}</Text>
              <Title level={3} style={{ margin: '8px 0 0' }}>
                {tenantsSummary.total}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Text type="secondary">{t('companiesPage.summary.active')}</Text>
              <Title level={3} style={{ margin: '8px 0 0', color: '#389e0d' }}>
                {tenantsSummary.active}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Text type="secondary">{t('companiesPage.summary.blocked')}</Text>
              <Title level={3} style={{ margin: '8px 0 0', color: '#cf1322' }}>
                {tenantsSummary.blocked}
              </Title>
            </Card>
          </Col>
        </Row>

        <Flex
          justify="space-between"
          align="center"
          style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}
        >
          <Space style={{ display: 'flex', alignItems: 'center' }}>
            <SettingOutlined style={{ color: '#1677ff' }} />
            <Text type="secondary">{t('companiesPage.helperText')}</Text>
          </Space>

          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/dashboard')}
              style={{ marginRight: '10px' }}
            >
              {t('companiesPage.actions.back')}
            </Button>
            <Button icon={<RedoOutlined />} onClick={fetchTenants}>
              {t('companiesPage.actions.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => setModalCreateVisible(true)}
            >
              {t('companiesPage.actions.newTenant')}
            </Button>
          </Space>
        </Flex>
      </S.Card>

      <S.Card>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}
        >
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>
            {t('companiesPage.table.plans.title')}
          </h2>
          <Button type="primary" onClick={openCreatePlanModal}>
            {t('companiesPage.actions.newPlan')}
          </Button>
        </Flex>
        <Table
          rowKey="id"
          loading={loadingPlans}
          pagination={{ pageSize: 5, showSizeChanger: false }}
          dataSource={plans}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('companiesPage.table.plans.empty')}
              />
            ),
          }}
          columns={[
            {
              title: t('companiesPage.table.plans.columns.code'),
              dataIndex: 'code',
              key: 'code',
              width: 120,
            },
            {
              title: t('companiesPage.table.plans.columns.name'),
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: t('companiesPage.table.plans.columns.monthlyFee'),
              dataIndex: 'monthly_price_cents',
              key: 'monthly_price_cents',
              render: (value: number) =>
                `${t('companiesPage.shared.currencyPrefix')} ${(value / 100).toFixed(2)}`,
            },
            {
              title: t('companiesPage.table.plans.columns.limits'),
              key: 'limits',
              render: (_: any, record: IPlan) =>
                t('companiesPage.table.plans.limitSummary', {
                  users: record.max_users,
                  professionals: record.max_professionals,
                  clients: record.max_clients,
                }),
            },
            {
              title: t('companiesPage.table.plans.columns.status'),
              dataIndex: 'status',
              key: 'status',
              render: (value: boolean) => (
                <Tag color={value ? 'success' : 'default'}>
                  {getPlanEnabledStatusLabel(value, t)}
                </Tag>
              ),
            },
            {
              title: t('companiesPage.table.plans.columns.landing'),
              key: 'landing_visible',
              render: (_: any, record: IPlan) =>
                record.landing_visible ? (
                  <Space size={6} wrap>
                    <Tag color="blue">{t('companiesPage.landing.visible')}</Tag>
                    {record.landing_highlight ? (
                      <Tag color="gold">{record.landing_highlight}</Tag>
                    ) : null}
                  </Space>
                ) : (
                  <Tag>{t('companiesPage.landing.hidden')}</Tag>
                ),
            },
            {
              title: t('companiesPage.table.plans.columns.actions'),
              key: 'actions',
              render: (_: any, record: IPlan) => (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEditPlanModal(record)}
                >
                  {t('companiesPage.actions.edit')}
                </Button>
              ),
            },
          ]}
        />
      </S.Card>

      <S.Card>
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>
          {t('companiesPage.table.tenants.title')}
        </h2>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loadingTenants}
          scroll={{ y: 400 }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('companiesPage.table.tenants.empty')}
              />
            ),
          }}
        />
      </S.Card>

      <TenantCreateEdit
        open={modalCreateVisible}
        close={() => setModalCreateVisible(false)}
        loading={creatingTenant}
        existingTenants={tenants.map((item) => item.name)}
        onCreate={handleCreateTenant}
      />

      <Modal
        title={
          editingPlan
            ? t('companiesPage.planModal.titleEdit', {
                name: editingPlan.name,
              })
            : t('companiesPage.planModal.titleCreate')
        }
        visible={planModalVisible}
        onOk={handleSavePlan}
        onCancel={() => {
          setPlanModalVisible(false);
          setEditingPlan(null);
          setPlanForm(emptyPlanForm);
        }}
        confirmLoading={savingPlan}
        okText={t('companiesPage.planModal.saveButton')}
        cancelText={t('common.cancel')}
      >
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label>{t('companiesPage.planModal.fields.code')}</label>
            <Input
              value={planForm.code}
              onChange={(e) =>
                setPlanForm({ ...planForm, code: e.target.value })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.name')}</label>
            <Input
              value={planForm.name}
              onChange={(e) =>
                setPlanForm({ ...planForm, name: e.target.value })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.description')}</label>
            <Input.TextArea
              rows={3}
              value={planForm.description}
              onChange={(e) =>
                setPlanForm({ ...planForm, description: e.target.value })
              }
            />
          </div>
          <Divider style={{ margin: '4px 0' }}>
            {t('companiesPage.planModal.sections.landing')}
          </Divider>
          <div>
            <label>{t('companiesPage.planModal.fields.landingSubtitle')}</label>
            <Input
              value={planForm.landing_subtitle || ''}
              onChange={(e) =>
                setPlanForm({ ...planForm, landing_subtitle: e.target.value })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.landingPrice')}</label>
            <Input
              placeholder={t(
                'companiesPage.planModal.fields.landingPricePlaceholder',
              )}
              value={planForm.landing_price_label || ''}
              onChange={(e) =>
                setPlanForm({
                  ...planForm,
                  landing_price_label: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label>
              {t('companiesPage.planModal.fields.landingFrequency')}
            </label>
            <Input
              placeholder={t(
                'companiesPage.planModal.fields.landingFrequencyPlaceholder',
              )}
              value={planForm.landing_frequency_label || ''}
              onChange={(e) =>
                setPlanForm({
                  ...planForm,
                  landing_frequency_label: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label>
              {t('companiesPage.planModal.fields.landingHighlight')}
            </label>
            <Input
              placeholder={t(
                'companiesPage.planModal.fields.landingHighlightPlaceholder',
              )}
              value={planForm.landing_highlight || ''}
              onChange={(e) =>
                setPlanForm({ ...planForm, landing_highlight: e.target.value })
              }
            />
          </div>
          <div>
            <label>
              {t('companiesPage.planModal.fields.landingSortOrder')}
            </label>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={planForm.landing_sort_order}
              onChange={(value) =>
                setPlanForm({
                  ...planForm,
                  landing_sort_order: Number(value || 0),
                })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.landingFeatures')}</label>
            <Input.TextArea
              rows={6}
              placeholder={t(
                'companiesPage.planModal.fields.landingFeaturesPlaceholder',
              )}
              value={planFeaturesToText(planForm.landing_features)}
              onChange={(e) =>
                setPlanForm({
                  ...planForm,
                  landing_features: planFeaturesFromText(e.target.value),
                })
              }
            />
          </div>
          <div>
            <label>
              {t('companiesPage.planModal.fields.monthlyPriceCents')}
            </label>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={planForm.monthly_price_cents}
              onChange={(value) =>
                setPlanForm({
                  ...planForm,
                  monthly_price_cents: Number(value || 0),
                })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.maxUsers')}</label>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={planForm.max_users}
              onChange={(value) =>
                setPlanForm({ ...planForm, max_users: Number(value || 1) })
              }
            />
          </div>
          <div>
            <label>
              {t('companiesPage.planModal.fields.maxProfessionals')}
            </label>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={planForm.max_professionals}
              onChange={(value) =>
                setPlanForm({
                  ...planForm,
                  max_professionals: Number(value || 1),
                })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.maxClients')}</label>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={planForm.max_clients}
              onChange={(value) =>
                setPlanForm({ ...planForm, max_clients: Number(value || 1) })
              }
            />
          </div>
          <div>
            <label>{t('companiesPage.planModal.fields.defaultDueDate')}</label>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={planForm.recommended_due_date}
              onChange={(value) =>
                setPlanForm({
                  ...planForm,
                  recommended_due_date: Number(value || 5),
                })
              }
            />
          </div>
          <Checkbox
            checked={!!planForm.landing_visible}
            onChange={(e) =>
              setPlanForm({ ...planForm, landing_visible: e.target.checked })
            }
          >
            {t('companiesPage.planModal.checkboxes.landingVisible')}
          </Checkbox>
          <Checkbox
            checked={!!planForm.recommended_dental}
            onChange={(e) =>
              setPlanForm({ ...planForm, recommended_dental: e.target.checked })
            }
          >
            {t('companiesPage.planModal.checkboxes.recommendedDental')}
          </Checkbox>
          <Checkbox
            checked={!!planForm.recommended_medical}
            onChange={(e) =>
              setPlanForm({
                ...planForm,
                recommended_medical: e.target.checked,
              })
            }
          >
            {t('companiesPage.planModal.checkboxes.recommendedMedical')}
          </Checkbox>
          <Checkbox
            checked={!!planForm.recommended_aesthetics}
            onChange={(e) =>
              setPlanForm({
                ...planForm,
                recommended_aesthetics: e.target.checked,
              })
            }
          >
            {t('companiesPage.planModal.checkboxes.recommendedAesthetics')}
          </Checkbox>
          <Checkbox
            checked={!!planForm.recommended_show_client_code}
            onChange={(e) =>
              setPlanForm({
                ...planForm,
                recommended_show_client_code: e.target.checked,
              })
            }
          >
            {t('companiesPage.planModal.checkboxes.recommendedShowClientCode')}
          </Checkbox>
          <Checkbox
            checked={planForm.status !== false}
            onChange={(e) =>
              setPlanForm({ ...planForm, status: e.target.checked })
            }
          >
            {t('companiesPage.planModal.checkboxes.status')}
          </Checkbox>
          <Divider style={{ margin: '8px 0' }}>
            {t('companiesPage.planModal.sections.allowedModules')}
          </Divider>
          <Text
            type="secondary"
            style={{ display: 'block', marginBottom: 10, fontSize: 12 }}
          >
            {t('companiesPage.planModal.allowedModulesHelper')}
          </Text>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}
          >
            {PLAN_MODULE_KEYS.map((moduleKey) => {
              const checked = Array.isArray(planForm.allowed_modules)
                ? planForm.allowed_modules.includes(moduleKey)
                : false;
              return (
                <Checkbox
                  key={moduleKey}
                  checked={checked}
                  onChange={(e) => {
                    const current = Array.isArray(planForm.allowed_modules)
                      ? planForm.allowed_modules
                      : [];
                    const next = e.target.checked
                      ? [...current, moduleKey]
                      : current.filter((k) => k !== moduleKey);
                    setPlanForm({ ...planForm, allowed_modules: next });
                  }}
                >
                  {getModuleLabel(moduleKey, t)}
                </Checkbox>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        title={t('companiesPage.configModal.title', {
          name: selectedTenant?.name || '',
        })}
        visible={modalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setModalVisible(false)}
        confirmLoading={savingConfig}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        {loadingConfig ? (
          <Spin />
        ) : config ? (
          <div>
            <div style={{ marginBottom: '10px' }}>
              <Checkbox
                checked={config.blocked}
                onChange={(e) =>
                  setConfig({ ...config, blocked: e.target.checked })
                }
              >
                {t('companiesPage.configModal.checkboxes.blockClient')}
              </Checkbox>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <Checkbox
                checked={config.dental}
                onChange={(e) =>
                  setConfig({ ...config, dental: e.target.checked })
                }
              >
                {t('companiesPage.configModal.checkboxes.dentalModule')}
              </Checkbox>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <Checkbox
                checked={config.medical}
                onChange={(e) =>
                  setConfig({ ...config, medical: e.target.checked })
                }
              >
                {t('companiesPage.configModal.checkboxes.medicalModule')}
              </Checkbox>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <Checkbox
                checked={config.aesthetics}
                onChange={(e) =>
                  setConfig({ ...config, aesthetics: e.target.checked })
                }
              >
                {t('companiesPage.configModal.checkboxes.aestheticsModule')}
              </Checkbox>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <Checkbox
                checked={config.show_client_code}
                onChange={(e) =>
                  setConfig({ ...config, show_client_code: e.target.checked })
                }
              >
                {t('companiesPage.configModal.checkboxes.showClientCode')}
              </Checkbox>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label>
                {t('companiesPage.configModal.fields.autoConfirmationChannel')}
              </label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={config.auto_schedule_confirmation_channel || 'both'}
                onChange={(value) =>
                  setConfig({
                    ...config,
                    auto_schedule_confirmation_channel: value,
                  })
                }
                options={autoConfirmationChannelOptions}
              />
            </div>
            <div>
              <Divider style={{ marginTop: 0 }}>
                {t('companiesPage.configModal.sections.planManager')}
              </Divider>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.planSuggestions')}
                </label>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t(
                    'companiesPage.configModal.fields.planSuggestionsPlaceholder',
                  )}
                  onChange={applyPlanSuggestion}
                  allowClear
                  value={config.plan_id}
                >
                  {planSuggestions.map((plan) => (
                    <Select.Option key={plan.id} value={plan.id}>
                      {plan.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Card size="small" style={{ background: '#fafafa' }}>
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    <Text strong>
                      {config.plan?.name ||
                        t('companiesPage.configModal.planCard.noPlanSelected')}
                    </Text>
                    <Text type="secondary">
                      {t('companiesPage.configModal.planCard.code', {
                        code:
                          config.plan?.code ||
                          t('companiesPage.shared.notAvailable'),
                      })}
                    </Text>
                    <Text type="secondary">
                      {t('companiesPage.configModal.planCard.monthlyFee', {
                        amount: `${t('companiesPage.shared.currencyPrefix')} ${(
                          (config.plan?.monthly_price_cents || 0) / 100
                        ).toFixed(2)}`,
                      })}
                    </Text>
                    <Text type="secondary">
                      {t('companiesPage.configModal.planCard.basePlan', {
                        users: config.plan?.max_users || 0,
                        professionals: config.plan?.max_professionals || 0,
                        clients: config.plan?.max_clients || 0,
                      })}
                    </Text>
                    <Text type="secondary">
                      {config.plan?.description ||
                        t(
                          'companiesPage.configModal.planCard.descriptionEmpty',
                        )}
                    </Text>
                  </Space>
                </Card>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.planStatus')}
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={config.plan_status || 'active'}
                  onChange={(value) =>
                    setConfig({ ...config, plan_status: value })
                  }
                >
                  <Select.Option value="active">
                    {getPlanStatusLabel('active', t)}
                  </Select.Option>
                  <Select.Option value="trial">
                    {getPlanStatusLabel('trial', t)}
                  </Select.Option>
                  <Select.Option value="suspended">
                    {getPlanStatusLabel('suspended', t)}
                  </Select.Option>
                  <Select.Option value="canceled">
                    {getPlanStatusLabel('canceled', t)}
                  </Select.Option>
                </Select>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.planStartedAt')}
                </label>
                <Input
                  value={config.plan_started_at}
                  onChange={(e) =>
                    setConfig({ ...config, plan_started_at: e.target.value })
                  }
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.planEndsAt')}
                </label>
                <Input
                  value={config.plan_ends_at}
                  onChange={(e) =>
                    setConfig({ ...config, plan_ends_at: e.target.value })
                  }
                />
              </div>
              <Divider>
                {t('companiesPage.configModal.sections.overrides')}
              </Divider>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 12 }}
              >
                {t('companiesPage.configModal.overridesHelper')}
              </Text>
              <div style={{ marginBottom: '10px' }}>
                <label>{t('companiesPage.configModal.fields.maxUsers')}</label>
                <InputNumber
                  style={{ width: '100%' }}
                  value={config.max_users ?? undefined}
                  onChange={(value) =>
                    setConfig({ ...config, max_users: Number(value || 1) })
                  }
                  min={1}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.maxProfessionals')}
                </label>
                <InputNumber
                  style={{ width: '100%' }}
                  value={config.max_professionals ?? undefined}
                  onChange={(value) =>
                    setConfig({
                      ...config,
                      max_professionals: Number(value || 1),
                    })
                  }
                  min={1}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>
                  {t('companiesPage.configModal.fields.maxClients')}
                </label>
                <InputNumber
                  style={{ width: '100%' }}
                  value={config.max_clients ?? undefined}
                  onChange={(value) =>
                    setConfig({ ...config, max_clients: Number(value || 1) })
                  }
                  min={1}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label>{t('companiesPage.configModal.fields.dueDate')}</label>
                <InputNumber
                  style={{ width: '100%' }}
                  value={config.due_date}
                  onChange={(value) =>
                    setConfig({ ...config, due_date: Number(value || 5) })
                  }
                  min={1}
                />
              </div>
              <Divider style={{ margin: '8px 0' }}>
                {t('companiesPage.configModal.sections.moduleOverrides')}
              </Divider>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 10, fontSize: 12 }}
              >
                {t('companiesPage.configModal.moduleOverridesHelper')}
              </Text>
              <Checkbox
                style={{ marginBottom: 8 }}
                checked={Array.isArray(config.override_allowed_modules)}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    override_allowed_modules: e.target.checked
                      ? Array.isArray(config.allowed_modules)
                        ? [...config.allowed_modules]
                        : []
                      : null,
                  })
                }
              >
                {t('companiesPage.configModal.checkboxes.customizeModules')}
              </Checkbox>
              {Array.isArray(config.override_allowed_modules) && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 6,
                    marginTop: 6,
                  }}
                >
                  {PLAN_MODULE_KEYS.map((moduleKey) => {
                    const checked = (
                      config.override_allowed_modules as string[]
                    ).includes(moduleKey);
                    return (
                      <Checkbox
                        key={moduleKey}
                        checked={checked}
                        onChange={(e) => {
                          const current =
                            config.override_allowed_modules as string[];
                          const next = e.target.checked
                            ? [...current, moduleKey]
                            : current.filter((k) => k !== moduleKey);
                          setConfig({
                            ...config,
                            override_allowed_modules: next,
                          });
                        }}
                      >
                        {getModuleLabel(moduleKey, t)}
                      </Checkbox>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <p>{t('companiesPage.configModal.loadError')}</p>
        )}
      </Modal>
    </S.Container>
  );
};

export default Companies;
